import type { PoseId } from '@/content/articulation';

/**
 * A rendered demonstration of the 3D mouth (debug page, Render): a sequence of held sounds, each
 * a pose with its recording, a caption and a length. The mouth moves into each pose just before
 * its sound starts, holds while the camera turns toward three-quarter and back, and relaxes
 * after the last. Pure: the shape blend and camera turn at any time, so playback can follow the
 * audio clock and a frame-by-frame render gets the same frames.
 */
export interface Segment {
  pose: PoseId;
  /** Seconds the sound lasts (its recording's length, or a chosen hold). */
  seconds: number;
  caption: string;
}

export interface TimelineOptions {
  /** Silence before the first sound, between sounds and after the last (s). */
  lead: number;
  gap: number;
  tail: number;
  /** How far the camera turns toward three-quarter during each hold (0 none, 1 all the way). */
  turn: number;
}

export const TIMELINE_DEFAULTS: TimelineOptions = { lead: 0.6, gap: 0.7, tail: 0.8, turn: 0.9 };

/** When each segment sounds. */
export interface Placed extends Segment {
  start: number;
  end: number;
}

export function place(
  segments: readonly Segment[],
  o: TimelineOptions = TIMELINE_DEFAULTS,
): { placed: Placed[]; total: number } {
  let t = o.lead;
  const placed = segments.map((s, i) => {
    const start = t + (i > 0 ? o.gap : 0);
    t = start + s.seconds;
    return { ...s, start, end: t };
  });
  return { placed, total: t + o.tail };
}

/** How far into a move (0-1, eased). */
function ease(k: number): number {
  const c = Math.min(1, Math.max(0, k));
  return c * c * (3 - 2 * c);
}

/** The mouth at time t: blend `amount` from pose `from` to pose `to`. */
export interface Frame {
  from: PoseId;
  to: PoseId;
  amount: number;
  /** The camera's turn toward three-quarter (0 side). */
  turn: number;
  caption: string | null;
}

/** Moves start this long before their sound (the mouth gets there first). */
const ONSET = 0.35;
const MOVE = 0.4;

export function frameAt(t: number, placed: readonly Placed[], o: TimelineOptions): Frame {
  let frame: Frame = { from: 'rest', to: 'rest', amount: 1, turn: 0, caption: null };
  placed.forEach((s, i) => {
    const prev = placed[i - 1];
    const moveStart = prev ? prev.end : s.start - ONSET;
    const moveEnd = prev ? Math.max(prev.end + 0.05, s.start + 0.1) : s.start - ONSET + MOVE;
    if (t >= moveStart) {
      frame = {
        from: prev?.pose ?? 'rest',
        to: s.pose,
        amount: ease((t - moveStart) / (moveEnd - moveStart)),
        turn: 0,
        caption: t >= s.start && t <= s.end ? s.caption : null,
      };
    }
    // Turn during the hold, back to the side before it ends.
    const a = s.start + 0.5;
    const b = s.end - 0.3;
    if (t > a && t < b) frame.turn = o.turn * Math.sin((Math.PI * (t - a)) / (b - a)) ** 2;
  });
  const last = placed[placed.length - 1];
  if (last && t >= last.end)
    frame = {
      from: last.pose,
      to: 'rest',
      amount: ease((t - last.end) / 0.5),
      turn: 0,
      caption: null,
    };
  return frame;
}
